export const NURSERY_PRODUCT_TYPE_HIERARCHY_WILDCARDS = [
  "PRODUCT.Home & Garden.Nursery Equipment & Furniture.*",
  "PRODUCT.Clothing & Accessories.Children's Clothing.Babywear.*",
  "PRODUCT.Sports Equipment & Leisure.Toys & Games.Nursery Toys.*",
  "PRODUCT.Technology.Home and Personal Security.Smart Home Monitoring and Sensor",
];

export const HOME_PRODUCT_TYPE_HIERARCHY_WILDCARDS = [
  "PRODUCT.Home & Garden.Cooking & Dining.Tableware.*",
  "PRODUCT.Home & Garden.Cooking & Dining.Hot Drink Preparation.*",
  "PRODUCT.Home & Garden.Laundry, Cleaning & Storage.Storage & Organisation.*",
  "PRODUCT.Home & Garden.Furnishing & Decorative Accessories.*",
  "PRODUCT.Home & Garden.Garden & Outdoor.Outdoor Furniture.*",
  "PRODUCT.Home & Garden.Garden & Outdoor.Outdoor Accessories.*",
  "PRODUCT.Home & Garden.Furniture.*",
  "PRODUCT.Home & Garden.Linen.Bed Linen.*",
  "PRODUCT.Home & Garden.Linen.Table Linen.*",
];

export const FASHION_PRODUCT_TYPE_HIERARCHY_WILDCARDS = [
  "PRODUCT.Clothing & Accessories.Men's Clothing.*",
  "PRODUCT.Clothing & Accessories.Women's Clothing.*",
  "PRODUCT.Clothing & Accessories.Clothing Accessories.Bags, Wallets & Accessories.*",
  "PRODUCT.Clothing & Accessories.Clothing Accessories.Jewellery & Watches.*",
  "PRODUCT.Clothing & Accessories.Clothing Accessories.Women's Accessories.Fascinator",
  "PRODUCT.Clothing & Accessories.Clothing Accessories.Women's Accessories.Women's Scarf",
];

export const FASHION_PRODUCT_TYPE_HIERARCHY_EXCLUSIONS = [
  "PRODUCT.Clothing & Accessories.Men's Clothing.Men's Underwear & Socks.*",
  "PRODUCT.Clothing & Accessories.Women's Clothing.Lingerie & Hosiery.Sex Toy",
];

export const BEAUTY_PRODUCT_TYPE_HIERARCHY_WILDCARDS = [
  "PRODUCT.Health & Beauty.Fragrance.*",
  "PRODUCT.Health & Beauty.Makeup.*",
  "PRODUCT.Health & Beauty.Skincare & Haircare.*",
];

export const BEAUTY_PRODUCT_TYPE_HIERARCHY_EXCLUSIONS = [
  "PRODUCT.Health & Beauty.Skincare & Haircare.Haircare.Ladies Shavers & Epilators",
  "PRODUCT.Health & Beauty.Skincare & Haircare.Bath & Bodycare.Razor or Razor Blades",
];

type ProductTypeHierarchyCarrier = {
  productTypeHierarchy?: unknown;
  sellingProductType?: unknown;
};

export function getProductTypeHierarchy(
  product: ProductTypeHierarchyCarrier | null | undefined,
): string | undefined {
  const productTypeHierarchy = product?.productTypeHierarchy;
  const sellingProductType = product?.sellingProductType;

  const hierarchy =
    typeof productTypeHierarchy === "string" ? productTypeHierarchy : undefined;
  const sellingType =
    typeof sellingProductType === "string" ? sellingProductType : undefined;

  if (hierarchy && sellingType) {
    return `${hierarchy}.${sellingType}`;
  }

  return hierarchy ?? sellingType;
}

function normalizeWildcardPrefix(wildcard: string): string {
  return wildcard.endsWith(".*") ? wildcard.slice(0, -2) : wildcard;
}

export function matchesProductTypeHierarchyWildcard(
  productTypeHierarchy: string | null | undefined,
  wildcard: string,
): boolean {
  if (!productTypeHierarchy) return false;

  const prefix = normalizeWildcardPrefix(wildcard);
  return (
    productTypeHierarchy === prefix ||
    productTypeHierarchy.startsWith(`${prefix}.`)
  );
}

export function matchesAnyProductTypeHierarchyWildcard(
  productTypeHierarchy: string | null | undefined,
  wildcards: string[],
): boolean {
  return wildcards.some((wildcard) =>
    matchesProductTypeHierarchyWildcard(productTypeHierarchy, wildcard),
  );
}

export function isNurseryProductTypeHierarchy(
  productTypeHierarchy: string | null | undefined,
): boolean {
  return matchesAnyProductTypeHierarchyWildcard(
    productTypeHierarchy,
    NURSERY_PRODUCT_TYPE_HIERARCHY_WILDCARDS,
  );
}

export function isHomeProductTypeHierarchy(
  productTypeHierarchy: string | null | undefined,
): boolean {
  return matchesAnyProductTypeHierarchyWildcard(
    productTypeHierarchy,
    HOME_PRODUCT_TYPE_HIERARCHY_WILDCARDS,
  );
}

export function isFashionProductTypeHierarchy(
  productTypeHierarchy: string | null | undefined,
): boolean {
  if (!productTypeHierarchy) return false;

  const matchesFashionInclusion = matchesAnyProductTypeHierarchyWildcard(
    productTypeHierarchy,
    FASHION_PRODUCT_TYPE_HIERARCHY_WILDCARDS,
  );
  const matchesFashionExclusion = matchesAnyProductTypeHierarchyWildcard(
    productTypeHierarchy,
    FASHION_PRODUCT_TYPE_HIERARCHY_EXCLUSIONS,
  );

  return matchesFashionInclusion && !matchesFashionExclusion;
}

export function isBeautyProductTypeHierarchy(
  productTypeHierarchy: string | null | undefined,
): boolean {
  if (!productTypeHierarchy) return false;

  const matchesBeautyInclusion = matchesAnyProductTypeHierarchyWildcard(
    productTypeHierarchy,
    BEAUTY_PRODUCT_TYPE_HIERARCHY_WILDCARDS,
  );
  const matchesBeautyExclusion = matchesAnyProductTypeHierarchyWildcard(
    productTypeHierarchy,
    BEAUTY_PRODUCT_TYPE_HIERARCHY_EXCLUSIONS,
  );

  return matchesBeautyInclusion && !matchesBeautyExclusion;
}
